import { isDefined } from "@clipboard-health/util-ts";
import {
  areCommaSeparatedSetsEqual,
  defaultAgeGroups,
  defaultDaysOfWeek,
  defaultJobTypes,
  defaultShiftTypes,
} from "@src/appV2/redesign/PlacementCandidate/constants";

import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { DEFAULT_DISTANCE_FILTER_IN_MILES } from "../utils";

interface FilterFormValues {
  distanceInMiles?: number;
  jobType?: string;
  shiftTime?: string;
  workerTypes?: string;
  daysOfWeek?: string;
  state?: string;
  ageGroups?: string;
}

export function getDistanceFilterFormValue(
  filter: GetPlacementsForWorkplaceQuery["filter"] | undefined
): number {
  const distanceInMiles = filter?.distanceInMiles?.$lt;

  return isDefined(distanceInMiles) ? distanceInMiles : DEFAULT_DISTANCE_FILTER_IN_MILES;
}

export function getDistanceFilterToSubmit(
  distanceInMiles: number | undefined
): NonNullable<GetPlacementsForWorkplaceQuery["filter"]>["distanceInMiles"] {
  return {
    $lt: isDefined(distanceInMiles) ? distanceInMiles : DEFAULT_DISTANCE_FILTER_IN_MILES,
  };
}

export function countTotalAppliedFilters(
  watchedValues: Partial<FilterFormValues> | undefined,
  defaultWorkerTypes: string,
  defaultStates: string
): number {
  if (!watchedValues) {
    return 0;
  }

  let count = 0;

  const distance = isDefined(watchedValues.distanceInMiles)
    ? watchedValues.distanceInMiles
    : DEFAULT_DISTANCE_FILTER_IN_MILES;
  if (distance !== DEFAULT_DISTANCE_FILTER_IN_MILES) {
    count += 1;
  }

  if (
    isDefined(watchedValues.jobType) &&
    watchedValues.jobType !== "" &&
    !areCommaSeparatedSetsEqual(watchedValues.jobType, defaultJobTypes)
  ) {
    count += 1;
  }

  if (
    isDefined(watchedValues.shiftTime) &&
    watchedValues.shiftTime !== "" &&
    !areCommaSeparatedSetsEqual(watchedValues.shiftTime, defaultShiftTypes)
  ) {
    count += 1;
  }

  if (
    isDefined(watchedValues.daysOfWeek) &&
    watchedValues.daysOfWeek !== "" &&
    !areCommaSeparatedSetsEqual(
      watchedValues.daysOfWeek.replace(/\|/g, ","),
      defaultDaysOfWeek.replace(/\|/g, ",")
    )
  ) {
    count += 1;
  }

  if (
    isDefined(watchedValues.workerTypes) &&
    watchedValues.workerTypes !== "" &&
    !areCommaSeparatedSetsEqual(watchedValues.workerTypes, defaultWorkerTypes)
  ) {
    count += 1;
  }

  if (
    defaultStates.length > 0 &&
    isDefined(watchedValues.state) &&
    watchedValues.state !== "" &&
    !areCommaSeparatedSetsEqual(watchedValues.state, defaultStates)
  ) {
    count += 1;
  }

  if (
    isDefined(watchedValues.ageGroups) &&
    watchedValues.ageGroups !== "" &&
    !areCommaSeparatedSetsEqual(watchedValues.ageGroups, defaultAgeGroups)
  ) {
    count += 1;
  }

  return count;
}
